import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import { Alert, Box, Button, Grid, Paper, Stack } from '@mui/material'
import { t, Trans } from '@lingui/macro'

import colors from '@/assets/scss/_themes-vars.module.scss'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import TextInput from '@/components/ui/inputs/TextInput'

import { InsIdentitySearchErrors, InsIdentitySearchFilters } from '../model/patientDirectory.types'

type InsPatientSearchFormProps = {
  errors: InsIdentitySearchErrors
  filters: InsIdentitySearchFilters
  hasRequestError: boolean
  onChange: (patch: Partial<InsIdentitySearchFilters>) => void
  onClear: () => void
}

const inputSurfaceSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.gray50,
  },
}

export function InsPatientSearchForm({ errors, filters, hasRequestError, onChange, onClear }: InsPatientSearchFormProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: colors.gray200,
        borderRadius: 2,
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        p: { xs: 2.5, md: 4 },
      }}
    >
      <Stack spacing={3}>
        {hasRequestError && (
          <Alert severity="error">
            <Trans>Unable to search INS identities.</Trans>
          </Alert>
        )}
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} sm={6} lg={3}>
            <TextInput
              fullWidth
              label={t`Name (birth or used)`}
              onChange={(event) => onChange({ surname: event.target.value })}
              placeholder={t`Ex: Dupont`}
              sx={inputSurfaceSx}
              value={filters.surname}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextInput
              fullWidth
              label={t`Given name (birth or used)`}
              onChange={(event) => onChange({ givenName: event.target.value })}
              placeholder={t`Ex: Jean`}
              sx={inputSurfaceSx}
              value={filters.givenName}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <DatePickerInput
              fullWidth
              error={Boolean(errors.birthDate)}
              helperText={errors.birthDate}
              label={t`Date of birth (DD/MM/YYYY)`}
              onChange={(value) => onChange({ birthDate: value })}
              sx={inputSurfaceSx}
              value={filters.birthDate}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextInput
              fullWidth
              error={Boolean(errors.insNIR)}
              helperText={errors.insNIR}
              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
              label={t`INS matricule`}
              onChange={(event) => onChange({ insNIR: event.target.value.replace(/\D/g, '').slice(0, 15) })}
              placeholder={t`123456789012345`}
              sx={inputSurfaceSx}
              value={filters.insNIR}
            />
          </Grid>
        </Grid>
        <Box sx={{ borderTop: `1px solid ${colors.gray100}`, display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
          <Button
            color="primary"
            onClick={onClear}
            startIcon={<RefreshRoundedIcon />}
            sx={{
              fontWeight: 600,
              '& .MuiButton-startIcon svg': { transition: 'transform 0.5s ease' },
              '&:hover .MuiButton-startIcon svg': { transform: 'rotate(180deg)' },
            }}
            variant="text"
          >
            <Trans>Clear filters</Trans>
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}
